import React from 'react';
import { Link } from 'react-router-dom';
import { useI18n } from '../context/I18nContext';
import { ArrowRight, Sparkles, Globe, Shield, Lock, Network, Search } from 'lucide-react';

const HERO_BG = 'https://images.unsplash.com/photo-1509048191080-d2984bad6ae5?w=2200&q=85';
const LUGANO = 'https://images.pexels.com/photos/33847172/pexels-photo-33847172.jpeg';
const DUBAI = 'https://images.pexels.com/photos/30063501/pexels-photo-30063501.jpeg';
const MONACO = 'https://customer-assets.emergentagent.com/job_gn-watches/artifacts/72qqdppt_Gita-di-un-giorno-Montecarlo.jpg';

export default function Home() {
  const { t } = useI18n();

  const whyIcons = [Globe, Search, Shield, Lock, Sparkles, Network];

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative min-h-[90vh] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_BG} alt="" className="w-full h-full object-cover opacity-30" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#08231a]/70 via-[#08231a]/85 to-[#08231a]" />
          <div className="gn-grain" />
        </div>
        <div className="relative max-w-[1440px] mx-auto px-6 lg:px-12 py-32 grid lg:grid-cols-12 gap-12 items-center w-full">
          <div className="lg:col-span-12 max-w-4xl">
            <p className="gn-overline mb-8 gn-reveal" data-testid="hero-eyebrow">{t('home.eyebrow')}</p>
            <h1 className="font-serif text-[3.5rem] md:text-[5.5rem] lg:text-[7rem] leading-[0.95] text-[#f5f5f0] mb-8 gn-reveal gn-delay-1" data-testid="hero-title">
              {t('home.title_1')}<br/>
              <span className="italic text-[#c9a961]">{t('home.title_2')}</span>
            </h1>
            <p className="text-lg text-[#a3b5ad] max-w-xl mb-12 leading-relaxed gn-reveal gn-delay-2">{t('home.subtitle')}</p>
            <div className="flex flex-col sm:flex-row gap-4 gn-reveal gn-delay-3">
              <Link to="/catalog" className="gn-btn-primary" data-testid="hero-cta-catalog">
                {t('home.cta_explore')} <ArrowRight size={16} />
              </Link>
              <Link to="/sell" className="gn-btn-ghost" data-testid="hero-cta-sell">
                {t('home.cta_sell')}
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* VISION */}
      <section className="bg-[#05150f] py-24 lg:py-32" data-testid="vision-section">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-16 items-center">
          <div className="lg:col-span-5">
            <p className="gn-overline mb-6">{t('home.vision_eyebrow')}</p>
            <h2 className="font-serif text-4xl md:text-5xl text-[#f5f5f0] leading-tight">{t('home.vision_title')}</h2>
          </div>
          <div className="lg:col-span-7 space-y-6">
            <p className="text-lg text-[#a3b5ad] leading-relaxed font-light">{t('home.vision_body')}</p>
            <div className="grid grid-cols-3 gap-6 pt-6 border-t border-[#c9a961]/15">
              <div><p className="font-serif text-3xl text-[#c9a961]">3</p><p className="text-xs uppercase tracking-[0.2em] text-[#a3b5ad] mt-2">{t('home.stat_hubs')}</p></div>
              <div><p className="font-serif text-3xl text-[#c9a961]">∞</p><p className="text-xs uppercase tracking-[0.2em] text-[#a3b5ad] mt-2">{t('home.stat_pieces')}</p></div>
              <div><p className="font-serif text-3xl text-[#c9a961]">48h</p><p className="text-xs uppercase tracking-[0.2em] text-[#a3b5ad] mt-2">{t('home.stat_valuation')}</p></div>
            </div>
          </div>
        </div>
      </section>

      {/* WHERE */}
      <section className="max-w-[1440px] mx-auto px-6 lg:px-12 py-24 lg:py-32" data-testid="where-section">
        <div className="grid lg:grid-cols-12 gap-12 mb-16">
          <div className="lg:col-span-6">
            <p className="gn-overline mb-6">{t('home.where_eyebrow')}</p>
            <h2 className="font-serif text-4xl md:text-6xl text-[#f5f5f0] leading-tight">{t('home.where_title')}</h2>
          </div>
          <div className="lg:col-span-6 flex items-end">
            <p className="text-lg text-[#a3b5ad] leading-relaxed font-light">{t('home.where_body')}</p>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[{ name: 'Lugano', img: LUGANO, sub: t('home.hub_hq') }, { name: 'Dubai', img: DUBAI, sub: t('home.hub_me') }, { name: 'Monte Carlo', img: MONACO, sub: t('home.hub_eu') }].map((loc, i) => (
            <div key={loc.name} className={`gn-card overflow-hidden gn-reveal gn-delay-${i+1}`}>
              <div className="aspect-[4/5] overflow-hidden"><img src={loc.img} alt={loc.name} className="w-full h-full object-cover gn-img-hover" loading="lazy" /></div>
              <div className="p-6 border-t border-[#c9a961]/15">
                <p className="gn-overline">{loc.sub}</p>
                <h3 className="font-serif text-3xl text-[#f5f5f0] mt-2">{loc.name}</h3>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* end */}
    </div>
  );
}
